import { STATIC_INBOXES_IDS } from '@/components/Inbox/inbox.const.js';
import api from '@/api';
import state from './state';
import { MODULE_NAME } from './const';
import StoreModuleService from '../StoreModuleService';
import { SET_ALERTS, SET_ALERT_DATA, SET_PULLING_INTERVAL, SET_HIDE_ALERTS_BAR_FLAG, SET_UNREAD_STATS } from './mutations';
import AlertService from '@/utils/alerts/alert-service';
import pusherService, { PUSHER_USAGE_TYPES } from '@/utils/pusher';
import ALERT_STATUSES from '@/utils/alerts/alert-statuses.json';

const buildAlertsActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

const ALERT_UPDATED_EVENT_KEY = 'alert-updated';

// actions declarations
export const setAlerts = buildAlertsActionObject('setAlerts');
export const getUserAlerts = buildAlertsActionObject('getUserAlerts');
export const archiveAlert = buildAlertsActionObject('archiveAlert');
export const unArchiveAlert = buildAlertsActionObject('unArchiveAlert');
export const setAlertData = buildAlertsActionObject('setAlertData');
export const startPollingAlerts = buildAlertsActionObject('startPollingAlerts');
export const stopPollingAlerts = buildAlertsActionObject('stopPollingAlerts');
export const handleAlertUpdated = buildAlertsActionObject('handleAlertUpdated');
export const getAlert = buildAlertsActionObject('getAlert');
export const markAlertAsCompleted = buildAlertsActionObject('markAlertAsCompleted');
export const updateAlert = buildAlertsActionObject('updateAlert');
export const setIsAlertHidden = buildAlertsActionObject('setIsAlertHidden');
export const newAlertReceived = buildAlertsActionObject('newAlertReceived');
export const getAlertsByInboxId = buildAlertsActionObject('getAlertsByInboxId');
export const getAlertsStats = buildAlertsActionObject('getAlertsStats');
export const addUnreadAlertToInboxId = buildAlertsActionObject('addUnreadAlertToInboxId');
export const subtractUnreadAlertToInboxId = buildAlertsActionObject('subtractUnreadAlertToInboxId');
export const updateUnreadStats = buildAlertsActionObject('updateUnreadStats');

const actions = {
	...StoreModuleService.createDefaultActions(),
	[startPollingAlerts.action]({ dispatch, commit }) {
		const NUM_OF_MINUTES = 5;
		const MS_IN_MINUTE = 60000;
		pusherService.subscribeToAlerts();
		pusherService.listenToEvents([
			pusherService.createPusherEventObj(
				ALERT_UPDATED_EVENT_KEY,
				(data) => dispatch(handleAlertUpdated.action, data),
				PUSHER_USAGE_TYPES.ALERT
			),
		]);
		dispatch(getUserAlerts.action);
		if (state.pullingInterval) {
			// no matter what, we prevent from two intervals going in the same time
			clearInterval(state.pullingInterval);
		}
		const interval = setInterval(() => dispatch(getUserAlerts.action), NUM_OF_MINUTES * MS_IN_MINUTE);
		commit(SET_PULLING_INTERVAL, interval);
	},
	[stopPollingAlerts.action]({ commit }) {
		pusherService.unsubscribeFromAlert(true);
		commit(SET_PULLING_INTERVAL, clearInterval(state.pullingInterval));
	},
	async [getUserAlerts.action]({ dispatch }) {
		const alerts = await api.getUserAlerts();
		const callback = () => {
			dispatch(getUserAlerts.action);
		};
		const clientAlerts = await AlertService.getClientAlerts(callback);
		dispatch(setAlerts.action, { data: [...clientAlerts, ...alerts] });
	},
	[setAlerts.action]({ commit }, payload) {
		const { data: alerts } = payload;
		const newAlerts = alerts.reduce((alertsMap, alert) => ({ ...alertsMap, [alert.id]: alert }), {});
		commit(SET_ALERTS, { ...state.alerts, ...newAlerts });
	},
	[setAlertData.action]({ commit, dispatch }, payload) {
		const { data: alertData } = payload;
		const previousAlertData = state.alerts[alertData.id];
		const shouldUpdateUnreadStats =
			!AlertService.isClientAlert(alertData) && (!previousAlertData || previousAlertData.archived !== alertData.archived);
		if (shouldUpdateUnreadStats) {
			const shouldSubtractFromUnread = previousAlertData && !previousAlertData.archived && alertData.archived;
			const updateUnreadAction = shouldSubtractFromUnread ? subtractUnreadAlertToInboxId.action : addUnreadAlertToInboxId.action;
			dispatch(updateUnreadAction, { inboxId: alertData.inbox || STATIC_INBOXES_IDS.PRIVATE_INBOX_ID }); // if an inbox id was not provided, it's not from shared inbox, it's from private inbox
		}
		commit(SET_ALERT_DATA, { key: alertData.id, value: alertData });
	},
	async [archiveAlert.action]({ dispatch }, payload) {
		const { id } = payload;
		await dispatch(updateAlert.action, { alert: { id, archived: true } });
	},
	async [unArchiveAlert.action]({ dispatch }, payload) {
		const { id } = payload;
		await dispatch(updateAlert.action, { alert: { id, archived: false } });
	},
	[handleAlertUpdated.action]({ dispatch }, payload) {
		const { pk: alertId } = payload;
		dispatch(getAlert.action, { id: alertId });
	},
	async [getAlert.action]({ dispatch }, payload) {
		const { id } = payload;
		const alert = await api.getAlert(id);
		dispatch(setAlertData.action, { data: alert });
	},
	async [getAlertsStats.action]({ dispatch }) {
		const stats = await api.getAlertsStats();
		dispatch(updateUnreadStats.action, { unread: stats });
	},
	async [addUnreadAlertToInboxId.action]({ dispatch, state }, payload) {
		const inboxId = payload.inboxId;
		dispatch(updateUnreadStats.action, { unread: { [inboxId]: (state.unread[inboxId] || 0) + 1 } });
	},
	async [subtractUnreadAlertToInboxId.action]({ dispatch, state }, payload) {
		const inboxId = payload.inboxId;
		dispatch(updateUnreadStats.action, { unread: { [inboxId]: (state.unread[inboxId] || 0) - 1 } });
	},
	[updateUnreadStats.action]({ commit, state }, payload) {
		commit(SET_UNREAD_STATS, { ...state.unread, ...payload.unread });
	},
	async [markAlertAsCompleted.action]({ dispatch }, payload) {
		const { id } = payload;
		const alertData = state.alerts[id];
		if (AlertService.isClientAlert(alertData)) {
			dispatch(updateAlert.action, { alert: { id, archived: true, status: ALERT_STATUSES.COMPLETED } });
		} else {
			const alert = await api.completeAlert(id);
			dispatch(setAlertData.action, { data: alert });
		}
	},
	async [getAlertsByInboxId.action]({ dispatch }, payload) {
		const { page, id, archived, cursor } = payload;
		const data = await api.getAlertsByInboxId(page, id, cursor, archived);
		dispatch(setAlerts.action, { data: data.results });
		return data;
	},
	async [updateAlert.action]({ dispatch }, payload) {
		const { alert } = payload;
		const alertData = state.alerts[alert.id];
		let newAlert;
		if (AlertService.isClientAlert(alertData)) {
			newAlert = AlertService.updateClientAlert(alert);
		} else {
			newAlert = await api.updateAlert(alert);
		}
		dispatch(setAlertData.action, { data: newAlert });
	},
	[setIsAlertHidden.action]({ commit }, payload) {
		commit(SET_HIDE_ALERTS_BAR_FLAG, payload);
	},
	[newAlertReceived.action]({ dispatch }) {
		dispatch(setIsAlertHidden.action, { value: false });
	},
};

export default actions;
